import {
  AppDataApiFactory,
  MultiTenancySides,
  TenantApiFactory,
  UserOverrideInput,
  UserOverrideOutput,
} from "./../data/service-proxies/api";
import { AuthProvider } from "react-admin";
import { UserManager, User, Profile } from "oidc-client";
import { History } from "history";
import messages from "../i18n/messages";
import memoizeOne from "memoize-one";
import { ProLogsPermissions } from "./permissions";
import { parseJwt } from "../util/parseJwt";
import { localStorageService } from "../util/localStorageService";

const ADMIN_USER_OVERRIDE = "admin_user_override";
const USER_OVERRIDE_RENEWAL_WINDOW_SECONDS = 60;

declare global {
  interface Window {
    _env: any;
  }
}

export interface ProLogsUserProfile extends Profile {
  multitenancy_side?: MultiTenancySides;
  tenant_master?: string;
  role_dashboard?: string;
}

export interface IAuthManagerSettings {
  authority: string;
  clientId: string;
  redirectUri: string;
  silentRedirectUri: string;
  postLogoutRedirectUri: string;
  requestedScopes?: string[] | undefined;
}

interface GetAccessTokenOptions {
  ignoreUserOverride?: boolean;
}

/**
 * Why a sign-out happened. Only `user-click` and `idle-240` are deliberate; the
 * rest are the portal giving up on a failed request. The value travels to
 * identity as a query parameter on /connect/endsession, so a logout can be
 * attributed to a cause in telemetry instead of being reconstructed by hand.
 */
export type LogoutReason =
  | "user-click"
  | "idle-240"
  | "x-session-logout"
  | "api-401-after-recovery"
  | "silent-renew-failed"
  | "request-recovery-401"
  | "signalr-401"
  | "password-changed"
  | "server-maintenance";

interface LogoutFromIdentityProviderProps {
  redirectBack?: boolean;
  reason: LogoutReason;
  /** Short context, e.g. the failing path and status. Pass a path, not a URL. */
  detail?: string;
}

const LOGOUT_DETAIL_MAX_LENGTH = 120;

/**
 * Several places navigate to /logout - the idle watcher, the password-changed
 * hub event, the maintenance dialog - so a bare /logout is the only one that is
 * really someone clicking Logout.
 */
export const readLogoutReason = (search: string): LogoutReason => {
  const reason = new URLSearchParams(search).get("reason");
  switch (reason) {
    case "idle-240":
    case "password-changed":
    case "server-maintenance":
      return reason;
    default:
      return "user-click";
  }
};

/**
 * Identity ignores query parameters it does not know, so these ride along on the
 * existing /connect/endsession redirect. Keep them short: that URL already
 * carries an id token.
 */
export const buildLogoutQueryParams = (
  params: LogoutFromIdentityProviderProps
): Record<string, string> => {
  const queryParams: Record<string, string> = { logout_reason: params.reason };
  if (params.detail) {
    queryParams["logout_detail"] = params.detail.slice(
      0,
      LOGOUT_DETAIL_MAX_LENGTH
    );
  }

  return queryParams;
};

class UserOverride implements UserOverrideOutput {
  name?: string | null;
  accessToken?: string | null;
  id?: string;

  constructor(deserialized?: UserOverrideOutput, serialized?: string) {
    if (deserialized) {
      this.name = deserialized.name;
      this.accessToken = deserialized.accessToken;
      this.id = deserialized.id;
    } else if (serialized) {
      const self = JSON.parse(serialized) as UserOverrideOutput;
      this.name = self.name;
      this.accessToken = self.accessToken;
      this.id = self.id;
    } else {
      throw new Error(
        "The user override output must be supplied, either deserialized or serialized"
      );
    }
  }

  getUser(): User | null {
    if (!this.accessToken) {
      return null;
    }

    const parsedAccessToken = parseJwt(this.accessToken);
    return new User({
      id_token: "",
      access_token: this.accessToken,
      token_type: "jwt",
      scope: parsedAccessToken.scope,
      refresh_token: "",
      session_state: "",
      expires_at: parsedAccessToken.exp,
      state: "",
      profile: parsedAccessToken,
    });
  }

  expiresWithin(seconds: number): boolean {
    if (!this.accessToken) {
      return true;
    }

    const parsedAccessToken = parseJwt(this.accessToken);
    return parsedAccessToken.exp * 1000 <= Date.now() + seconds * 1000;
  }

  getMode(): string | undefined {
    return this.accessToken
      ? parseJwt(this.accessToken).impersonation_mode
      : undefined;
  }
}

export class AuthManager implements AuthProvider {
  private userManager?: UserManager;
  private userOverride?: UserOverride;
  private userOverrideRenewal?: Promise<void>;
  private silentSigninPromise?: Promise<User>;
  private unauthorizedLogoutPromise?: Promise<void>;
  private readonly history: History<unknown>;
  private readonly memoizedGetPermissions: () => Promise<ProLogsPermissions>;
  requestedScopes: string[];

  constructor(history: History<unknown>) {
    const override = sessionStorage.getItem(ADMIN_USER_OVERRIDE);
    if (override) {
      this.userOverride = new UserOverride(undefined, override);
    }

    this.memoizedGetPermissions = memoizeOne(
      async () => await this.getPermissionsFromApi()
    );
    this.history = history;
    this.requestedScopes = [
      "openid",
      "profile",
      "email",
      "offline_access",
      "EldApi",
      "role",
    ];
  }

  init = (settings: IAuthManagerSettings) => {
    if (settings.requestedScopes) {
      this.requestedScopes = [
        ...this.requestedScopes,
        ...settings.requestedScopes,
      ];
    }

    this.userManager = new UserManager({
      client_id: settings.clientId,
      authority: settings.authority,
      redirect_uri: settings.redirectUri,
      silent_redirect_uri: settings.silentRedirectUri,
      post_logout_redirect_uri: settings.postLogoutRedirectUri,
      response_type: "code",
      scope: this.requestedScopes.reduce(
        (prevState, current) => prevState + " " + current,
        ""
      ),
      filterProtocolClaims: true,
      loadUserInfo: true,
      automaticSilentRenew: true,
    });

    // Oidc.Log.logger = console;
    // Oidc.Log.level = Oidc.Log.DEBUG;
  };

  addUserLoadedEvent = (fn: (user: User) => void) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    const callback = (user: User) => {
      fn(user);
      this.userManager?.events.removeUserLoaded(callback);
    };

    this.userManager.events.addUserLoaded(callback);
  };

  login = async (params: any) => {
    // By default, react-admin calls this on Login component submit.
    // Instead, we call it as soon as the Login component renders.

    if (params && params.returnTo) {
      localStorageService.setRedirect(this.history.location);
    }

    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    return await this.userManager.signinRedirect();
  };

  handleAuthentication = async () => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    try {
      const user = await this.userManager.signinCallback();
      const location = localStorageService.getRedirect();
      if (location) {
        document.location.href = !location.search
          ? location.pathname
          : location.pathname + location.search;
      }

      return user;
    } catch {
      document.location.href = "/";
    }
  };

  logout = async (params: any) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    this.userOverride = undefined;
    sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
    const user = await this.userManager.getUser();
    if (user) {
      return "/logout";
    }

    return;
  };

  logoutFromIdentityProvider = async (
    params: LogoutFromIdentityProviderProps
  ) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    this.userOverride = undefined;
    sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
    const user = await this.userManager.getUser();
    if (user) {
      if (params.redirectBack) {
        localStorageService.setRedirect(this.history.location);
      }

      await this.userManager.signoutRedirect({
        extraQueryParams: buildLogoutQueryParams(params),
      });
    }
  };

  checkAuth = async (params: any = {}) => {
    // Each time the user navigates, react-admin calls the authProvider.checkAuth() method,
    // so it’s the ideal place to validate the credentials.
    // If the promise is rejected, react-admin redirects by default to the /login page.
    // You can override where to redirect the user in checkAuth(), by rejecting an object with a redirectTo property.
    // Note that react-admin will call the authProvider.logout() method before redirecting.
    // If you specify the redirectTo here, it will override the url which may have been returned by the call to logout().
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    await this.ensureUserOverrideIsCurrent();
    let user =
      this.userOverride?.getUser() ?? (await this.userManager.getUser());
    const redirect = localStorageService.getRedirect(true);
    if (!user || user.expired) {
      if (
        params !== null &&
        this.history.location.pathname !== "/callback" &&
        this.history.location.pathname !== "/logged-out" &&
        !redirect
      ) {
        localStorageService.setRedirect(this.history.location);
      }

      return Promise.reject();
    }

    return Promise.resolve();
  };

  checkError = (error: any) => {
    // each time the API returns an error, react-admin calls the authProvider.checkError() method.
    // Decide which HTTP status codes should let the user continue (by returning a resolved promise)
    // or log them out (by returning a rejected promise).
    const status = error.status;
    if (status === 401) {
      // log out
      return Promise.reject();
    }

    // continue
    return Promise.resolve();
  };

  getAccessToken = async (options?: GetAccessTokenOptions) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    if (!options?.ignoreUserOverride) {
      await this.ensureUserOverrideIsCurrent();
    }

    const user = options?.ignoreUserOverride
      ? await this.userManager.getUser()
      : this.userOverride?.getUser() ?? (await this.userManager.getUser());
    if (!user) {
      return Promise.reject();
    }

    return user.access_token;
  };

  recoverOidcSession = async (
    rejectedAccessToken?: string,
    options?: { allowWhileImpersonating?: boolean }
  ) => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    // Recovering the master session hands back the master's token, which must
    // never become the token a request or hub connection retries with while an
    // override is active. The only caller allowed past this point is
    // recoverUserOverride, which mints a fresh override afterwards.
    if (this.userOverride && !options?.allowWhileImpersonating) {
      return Promise.reject();
    }

    const currentUser = await this.userManager.getUser();
    if (
      currentUser &&
      rejectedAccessToken &&
      currentUser.access_token !== rejectedAccessToken
    ) {
      return currentUser.access_token;
    }

    let signinPromise = this.silentSigninPromise;
    if (!signinPromise) {
      signinPromise = this.userManager.signinSilent();
      this.silentSigninPromise = signinPromise;
    }

    try {
      const renewedUser = await signinPromise;
      if (!renewedUser?.access_token) {
        throw new Error("Silent sign-in did not return an access token");
      }

      return renewedUser.access_token;
    } catch (error) {
      await this.logoutAfterUnauthorized("silent-renew-failed");
      throw error;
    } finally {
      if (this.silentSigninPromise === signinPromise) {
        this.silentSigninPromise = undefined;
      }
    }
  };

  logoutAfterUnauthorized = async (reason: LogoutReason, detail?: string) => {
    if (!this.unauthorizedLogoutPromise) {
      this.unauthorizedLogoutPromise = this.logoutFromIdentityProvider({
        redirectBack: true,
        reason,
        detail,
      });
    }

    return await this.unauthorizedLogoutPromise;
  };

  getPermissions = async (params: any) => {
    const user =
      this.userOverride?.getUser() ?? (await this.userManager?.getUser());
    if (!user) {
      console.warn(
        `Requested permissions by '${this.history.location.pathname}', but the user is not loaded`
      );
      return {};
    }

    const perms = await this.memoizedGetPermissions();
    return perms;
  };

  getIdentity = async () => {
    if (!this.userManager) {
      throw new Error(messages.AUTH_CALL_INIT_FIRST);
    }

    const user =
      this.userOverride?.getUser() ?? (await this.userManager.getUser());
    if (!user) {
      return { id: "" };
    }

    const { sub, ...profile } = user.profile as ProLogsUserProfile;
    const getFullName = () => {
      if (this.isUserOverride()) {
        return `MASTER as ${this.userOverride?.name} Admin`;
      } else {
        return profile.multitenancy_side === MultiTenancySides.Host
          ? `${profile.tenant_master ?? "MASTER"}/${profile.given_name} ${
              profile.family_name
            }`
          : `${profile.given_name} ${profile.family_name}`;
      }
    };

    return {
      ...profile,
      id: sub,
      fullName: getFullName(),
    };
  };

  setUserOverride = async (input?: UserOverrideInput) => {
    if (!input) {
      this.userOverride = undefined;
      sessionStorage.removeItem(ADMIN_USER_OVERRIDE);
      return;
    }

    const renewedOverride = await this.acquireUserOverride(input);
    sessionStorage.setItem(
      ADMIN_USER_OVERRIDE,
      JSON.stringify(renewedOverride)
    );
    this.userOverride = renewedOverride;
  };

  private acquireUserOverride = async (input: UserOverrideInput) => {
    const result = await TenantApiFactory(
      undefined,
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327"
    ).apiAppTenantAcquiretenantadminaccesstokenPost(
      {
        ...input,
        requestedScopes: this.requestedScopes,
      },
      {
        headers: {
          Authorization:
            "Bearer " +
            (await this.getAccessToken({ ignoreUserOverride: true })),
        },
      }
    );

    return new UserOverride(result.data);
  };

  private ensureUserOverrideIsCurrent = async () => {
    if (
      !this.userOverride ||
      !this.userOverride.id ||
      !this.userOverride.expiresWithin(USER_OVERRIDE_RENEWAL_WINDOW_SECONDS)
    ) {
      return;
    }

    const expiringOverride = this.userOverride;
    try {
      await this.renewUserOverride(expiringOverride);
    } catch (error) {
      if (
        this.userOverride === expiringOverride &&
        !expiringOverride.expiresWithin(0)
      ) {
        return;
      }

      throw error;
    }
  };

  private renewUserOverride = async (expiringOverride: UserOverride) => {
    if (!this.userOverrideRenewal) {
      const previousMode = expiringOverride.getMode();
      this.userOverrideRenewal = this.acquireUserOverride({
        id: expiringOverride.id!,
      })
        .then((renewedOverride) => {
          if (this.userOverride !== expiringOverride) {
            return;
          }

          sessionStorage.setItem(
            ADMIN_USER_OVERRIDE,
            JSON.stringify(renewedOverride)
          );
          this.userOverride = renewedOverride;
          if (previousMode !== renewedOverride.getMode()) {
            window.location.reload();
          }
        })
        .finally(() => {
          this.userOverrideRenewal = undefined;
        });
    }

    await this.userOverrideRenewal;
  };

  /**
   * Recovers from a 401 while impersonating. The impersonation token cannot be
   * refreshed, so recovery means minting a new one through the override
   * endpoint with the master's own token. Renewal is single-flight per tab, and
   * the caller always gets an override token back - never the master's.
   */
  recoverUserOverride = async (
    rejectedAccessToken?: string
  ): Promise<string> => {
    const rejectedOverride = this.userOverride;
    if (!rejectedOverride?.id) {
      return Promise.reject();
    }

    const currentAccessToken = rejectedOverride.getUser()?.access_token;
    if (
      rejectedAccessToken &&
      currentAccessToken &&
      currentAccessToken !== rejectedAccessToken
    ) {
      // Another request in this tab already minted a newer override.
      return currentAccessToken;
    }

    try {
      await this.renewUserOverride(rejectedOverride);
    } catch (error) {
      if (!this.isUnauthorizedError(error)) {
        throw error;
      }

      // The master's own token was rejected while minting, so the master
      // session is what needs recovering; mint the override again afterwards.
      await this.recoverOidcSession(undefined, {
        allowWhileImpersonating: true,
      });
      await this.renewUserOverride(this.userOverride ?? rejectedOverride);
    }

    const renewedAccessToken = this.userOverride?.getUser()?.access_token;
    if (!renewedAccessToken) {
      throw new Error(
        "The user override renewal did not return an access token"
      );
    }

    return renewedAccessToken;
  };

  isUserOverride = () => {
    return !!this.userOverride;
  };

  private getPermissionsFromApi = async (): Promise<ProLogsPermissions> => {
    try {
      const permissionsFromApi = (
        await this.requestWithOidcSessionRecovery((accessToken) =>
          AppDataApiFactory(
            undefined,
            process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
              "https://localhost:44327"
          ).apiAppAppdataUserpermissionsGet({
            headers: { Authorization: "Bearer " + accessToken },
          })
        )
      ).data;
      if (!permissionsFromApi) {
        return {};
      }

      const actionPermissions =
        permissionsFromApi.actionPermissionGroups?.reduce(
          (previousResult, currentGroup) => {
            const permissionsFromThisGroup =
              currentGroup.permissions
                ?.filter((x) => x && x.isGranted && x.name)
                .reduce(
                  (previousPermissions, currentPermission) => ({
                    ...previousPermissions,
                    [currentPermission.name!]: currentPermission.isGranted,
                  }),
                  {}
                ) ?? {};
            return { ...previousResult, ...permissionsFromThisGroup };
          },
          { __: false }
        ) ?? {};
      return {
        actionPermissions,
        fieldPermissions: permissionsFromApi.fieldPermissionGroups ?? [],
      };
    } catch {
      return {};
    }
  };

  private requestWithOidcSessionRecovery = async <T>(
    request: (accessToken: string) => Promise<T>
  ): Promise<T> => {
    const accessToken = await this.getAccessToken();

    try {
      return await request(accessToken);
    } catch (error) {
      if (!this.isUnauthorizedError(error)) {
        throw error;
      }

      const renewedAccessToken = this.userOverride
        ? await this.recoverUserOverride(accessToken)
        : await this.recoverOidcSession(accessToken);
      try {
        return await request(renewedAccessToken);
      } catch (retryError) {
        if (this.isUnauthorizedError(retryError)) {
          await this.logoutAfterUnauthorized("request-recovery-401");
        }

        throw retryError;
      }
    }
  };

  private isUnauthorizedError = (error: any) => {
    return error?.response?.status === 401 || error?.status === 401;
  };
}
